// =================================================================
// PasteAsFileWidget.js - Widget "Paste as File" Révolutionnaire
// Version compacte pour haut de colonne droite selon mockup V2
// =================================================================

import React, { useState, useRef } from 'react';
import { Clipboard, FileText, Code, Database, Upload, Download } from 'lucide-react';

const PasteAsFileWidget = ({ projectId, onFileSaved }) => {
  const [pasteContent, setPasteContent] = useState('');
  const [detectedType, setDetectedType] = useState(null);
  const [suggestedName, setSuggestedName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);

  // Auto-détection type contenu (scoring pondéré)
  // Important : Markdown prioritaire si marqueurs MD clairs, même si
  // des mots JS ("function", "const", "import") traînent dans le texte.
  const detectContentType = (content) => {
    if (!content.trim()) return null;
    const lines = content.split('\n');

    // ─── Score Markdown ───
    const mdHeaderCount = lines.filter(l => /^#{1,6}\s+\S/.test(l)).length;
    const mdListCount = lines.filter(l => /^\s*[-*+]\s+\S/.test(l) || /^\s*\d+\.\s+\S/.test(l)).length;
    const mdCodeFence = (content.match(/```/g) || []).length >= 2;
    const mdBold = (content.match(/\*\*[^*\n]+\*\*/g) || []).length;
    const mdLink = (content.match(/\[[^\]]+\]\([^)]+\)/g) || []).length;
    const mdQuote = lines.filter(l => /^>\s+\S/.test(l)).length;

    const mdScore =
      (mdHeaderCount >= 1 ? 3 : 0) +
      (mdHeaderCount >= 2 ? 2 : 0) +
      (mdListCount >= 2 ? 2 : 0) +
      (mdCodeFence ? 2 : 0) +
      (mdBold >= 1 ? 1 : 0) +
      (mdLink >= 1 ? 1 : 0) +
      (mdQuote >= 1 ? 1 : 0);

    // MD clair (score ≥ 3) → markdown, même si du code JS est présent dans le texte
    if (mdScore >= 3) {
      return { type: 'markdown', ext: 'md', icon: <FileText size={12} />, color: '#083fa1' };
    }

    // ─── HTML ───
    if (/<(html|!DOCTYPE|body|head|div|span|p|h[1-6])[^>]*>/i.test(content)) {
      return { type: 'html', ext: 'html', icon: <Code size={12} />, color: '#e34c26' };
    }

    // ─── JavaScript / TypeScript (stricte : ≥2 marqueurs structurels) ───
    const jsMarkers = [
      /\bfunction\s+\w+\s*\(/,
      /\bconst\s+\w+\s*=/,
      /\blet\s+\w+\s*=/,
      /=>\s*[{(]/,
      /\bimport\s+[\w{},\s*]+\s+from\s+['"]/,
      /\bexport\s+(default|const|function|class)/,
      /\brequire\s*\(\s*['"]/,
      /\bclass\s+\w+\s*(\{|extends)/,
    ];
    const jsMatches = jsMarkers.filter(re => re.test(content)).length;
    if (jsMatches >= 2) {
      return { type: 'javascript', ext: 'js', icon: <Code size={12} />, color: '#f7df1e' };
    }

    // ─── SQL ───
    if (/\b(SELECT\s+|INSERT\s+INTO|UPDATE\s+\w+\s+SET|DELETE\s+FROM|CREATE\s+TABLE|ALTER\s+TABLE)\b/i.test(content)) {
      return { type: 'sql', ext: 'sql', icon: <Database size={12} />, color: '#336791' };
    }

    // ─── CSV — lignes avec un nombre consistant de séparateurs ───
    const csvLines = lines.filter(l => l.trim() && l.includes(','));
    if (csvLines.length >= 3) {
      const counts = csvLines.slice(0, 5).map(l => l.split(',').length);
      const allSame = counts.every(c => c === counts[0] && c >= 3);
      if (allSame) {
        return { type: 'csv', ext: 'csv', icon: <FileText size={12} />, color: '#16a34a' };
      }
    }

    // ─── Fallback markdown léger (si au moins 1 marqueur MD) ───
    if (mdScore >= 1) {
      return { type: 'markdown', ext: 'md', icon: <FileText size={12} />, color: '#083fa1' };
    }

    // ─── Text par défaut ───
    return { type: 'text', ext: 'txt', icon: <FileText size={12} />, color: '#6b7280' };
  };

  // Table des types disponibles pour le sélecteur manuel
  const AVAILABLE_TYPES = [
    { type: 'markdown', ext: 'md', label: '.md', icon: <FileText size={10} />, color: '#083fa1' },
    { type: 'text', ext: 'txt', label: '.txt', icon: <FileText size={10} />, color: '#6b7280' },
    { type: 'javascript', ext: 'js', label: '.js', icon: <Code size={10} />, color: '#f7df1e' },
    { type: 'html', ext: 'html', label: '.html', icon: <Code size={10} />, color: '#e34c26' },
    { type: 'sql', ext: 'sql', label: '.sql', icon: <Database size={10} />, color: '#336791' },
    { type: 'csv', ext: 'csv', label: '.csv', icon: <FileText size={10} />, color: '#16a34a' },
  ];

  // Changement manuel d'extension : met à jour detectedType + suggestedName
  const handleManualTypeChange = (newExt) => {
    const newType = AVAILABLE_TYPES.find(t => t.ext === newExt);
    if (!newType) return;
    setDetectedType({ ...newType, icon: newType.icon });
    // Remplace l'extension dans le nom suggéré (ou ajoute si absente)
    setSuggestedName(prev => {
      if (!prev) return `untitled.${newExt}`;
      return prev.match(/\.[^.]+$/) ? prev.replace(/\.[^.]+$/, `.${newExt}`) : `${prev}.${newExt}`;
    });
  };

  // Génération nom contextuel intelligent
  const generateContextualName = (content, type) => {
    const timestamp = new Date().toISOString().slice(0,16).replace('T', '_');
    const projectName = 'oracle_trya';
    
    let contextName = 'untitled';
    
    if (type === 'javascript') {
      if (content.includes('function ')) {
        const match = content.match(/function\s+(\w+)/);
        contextName = match ? match[1] : 'script';
      } else if (content.includes('class ')) {
        const match = content.match(/class\s+(\w+)/);
        contextName = match ? match[1] : 'component';
      } else {
        contextName = 'script';
      }
    } else if (type === 'markdown') {
      const firstHeader = content.match(/^#\s+(.+)/m);
      contextName = firstHeader ? firstHeader[1].toLowerCase().replace(/\s+/g, '_') : 'document';
    } else if (type === 'sql') {
      if (content.toUpperCase().includes('CREATE TABLE')) {
        const match = content.match(/CREATE TABLE\s+(\w+)/i);
        contextName = match ? match[1] : 'schema';
      } else {
        contextName = 'query';
      }
    }
    
    return `${projectName}_${contextName}_${timestamp}`;
  };

  // Handle paste event
  const handlePaste = async (e) => {
    const content = e.clipboardData.getData('text');
    if (content) {
      setPasteContent(content);
      const type = detectContentType(content);
      setDetectedType(type);
      
      if (type) {
        const name = generateContextualName(content, type.type);
        setSuggestedName(`${name}.${type.ext}`);
        setShowPreview(true);
      }
    }
  };

  // Handle textarea content change
  const handleContentChange = (e) => {
    const content = e.target.value;
    setPasteContent(content);
    
    if (content.trim()) {
      const type = detectContentType(content);
      setDetectedType(type);
      
      if (type) {
        const name = generateContextualName(content, type.type);
        setSuggestedName(`${name}.${type.ext}`);
        setShowPreview(true);
      }
    } else {
      setDetectedType(null);
      setSuggestedName('');
      setShowPreview(false);
    }
  };

  // Télécharger en .md
  const handleDownloadMd = () => {
    if (!pasteContent.trim()) return;
    const mdName = suggestedName ? suggestedName.replace(/\.[^.]+$/, '.md') : 'paste_content.md';
    const blob = new Blob([pasteContent], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = mdName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Sauvegarder comme fichier
  const handleSaveAsFile = async () => {
    if (!pasteContent.trim() || !detectedType) return;
    
    setIsProcessing(true);
    
    try {
      // API call pour sauver fichier
      const response = await fetch(`/api/projects/${projectId}/paste-as-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('oracle_token')}`
        },
        body: JSON.stringify({
          content: pasteContent,
          filename: suggestedName,
          contentType: detectedType.type,
          autoGenerated: true
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        onFileSaved && onFileSaved(result.file);
        
        // Reset widget
        setPasteContent('');
        setDetectedType(null);
        setSuggestedName('');
        setShowPreview(false);
        
        console.log('✅ Fichier sauvé:', result.file.filename);
      }
    } catch (error) {
      console.error('Erreur sauvegarde fichier:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="paste-widget-compact">
      <style>{`
        .paste-widget-compact {
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          margin: 8px 12px;
          overflow: hidden;
          margin-bottom: 6px;
        }

        .widget-header-compact {
          background: linear-gradient(135deg, rgba(72, 136, 224, 0.05) 0%, var(--accent-light) 100%);
          border-bottom: 1px solid var(--card-border);
          padding: 6px 10px;
        }

        .widget-title-compact {
          font-size: 11px;
          font-weight: 600;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .widget-subtitle-compact {
          font-size: 9px;
          color: var(--text-secondary);
          margin-top: 1px;
        }

        .paste-area-compact {
          padding: 6px 10px;
        }

        .paste-textarea-compact {
          width: 100%;
          min-height: 110px;
          max-height: 150px;
          border: 1px dashed var(--input-border);
          border-radius: 4px;
          padding: 8px;
          font-size: 11px;
          font-family: 'Monaco', 'Menlo', monospace;
          resize: vertical;
          background: var(--input-bg);
          color: var(--text);
          transition: all 0.2s ease;
        }

        .paste-textarea-compact:focus {
          outline: none;
          border-color: #1D4ED8;
          border-style: solid;
          background: var(--card-bg);
          box-shadow: 0 0 0 2px rgba(72, 136, 224, 0.1);
        }

        .paste-textarea-compact::placeholder {
          color: var(--text-muted);
          font-size: 10px;
        }

        .detection-preview-compact {
          margin-top: 8px;
          padding: 6px 8px;
          background: var(--input-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .type-indicator-compact {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .filename-input-compact {
          flex: 1;
          border: 1px solid var(--input-border);
          border-radius: 4px;
          padding: 4px 6px;
          font-size: 10px;
          background: var(--card-bg);
          color: var(--text);
        }

        .filename-input-compact:focus {
          outline: none;
          border-color: #1D4ED8;
          box-shadow: 0 0 0 1px rgba(72, 136, 224, 0.1);
        }

        .widget-actions-compact {
          display: flex;
          gap: 4px;
          margin-top: 6px;
        }

        .save-btn-compact {
          flex: 1;
          background: linear-gradient(135deg, #1D4ED8 0%, #1e40af 100%);
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .save-btn-compact:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(72, 136, 224, 0.3);
        }

        .save-btn-compact:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .cancel-btn-compact {
          padding: 6px 10px;
          background: var(--input-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          font-size: 10px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel-btn-compact:hover {
          background: var(--card-hover);
        }
      `}</style>

      {/* Header Widget Compact */}
      <div className="widget-header-compact">
        <div className="widget-title-compact">
          <Clipboard size={12} />
          Coller en fichier
        </div>
        <div className="widget-subtitle-compact">
          Auto-détection type
        </div>
      </div>

      {/* Zone paste compacte */}
      <div className="paste-area-compact">
        <textarea
          ref={textareaRef}
          className="paste-textarea-compact"
          placeholder="Collez votre contenu ici...
• Code JavaScript/HTML/CSS
• Documents Markdown
• Requêtes SQL • Données CSV"
          value={pasteContent}
          onChange={handleContentChange}
          onPaste={handlePaste}
        />

        {/* Prévisualisation détection */}
        {showPreview && detectedType && (
          <div className="detection-preview-compact">
            <div className="type-indicator-compact" style={{ color: detectedType.color }}>
              {detectedType.icon}
              {detectedType.type}
            </div>
            <input
              type="text"
              className="filename-input-compact"
              value={suggestedName}
              onChange={(e) => setSuggestedName(e.target.value)}
              placeholder="nom_fichier.ext"
            />
            {/* Sélecteur d'extension manuel — permet de forcer .md quand détection mixte */}
            <select
              className="ext-selector-compact"
              value={detectedType.ext}
              onChange={(e) => handleManualTypeChange(e.target.value)}
              title="Forcer une extension"
              style={{
                padding: '4px 6px',
                background: 'var(--input-bg)',
                color: detectedType.color,
                border: '1px solid var(--card-border)',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: 4,
                outline: 'none',
              }}
            >
              {AVAILABLE_TYPES.map(t => (
                <option key={t.ext} value={t.ext} style={{ background: 'var(--card-bg)', color: 'var(--text)' }}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Actions */}
        {showPreview && (
          <div className="widget-actions-compact">
            <button
              className="save-btn-compact"
              onClick={handleSaveAsFile}
              disabled={isProcessing || !pasteContent.trim()}
            >
              <Upload size={10} />
              {isProcessing ? 'Saving...' : 'Sauver'}
            </button>
            <button
              className="cancel-btn-compact"
              onClick={handleDownloadMd}
              disabled={!pasteContent.trim()}
              title="Télécharger en .md"
            >
              <Download size={10} style={{ marginRight: '3px', display: 'inline' }} />
              .md
            </button>
            <button
              className="cancel-btn-compact"
              onClick={() => {
                setPasteContent('');
                setDetectedType(null);
                setSuggestedName('');
                setShowPreview(false);
              }}
            >
              Vider
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasteAsFileWidget;
